<template>
  <settings-checker>
    <div class="booking">
      <div class="booking-wrapper">
        <petstar-ribbon/>
        <div class="booking-divider booking-divider-left">
          <div class="booking-content">
            <booking-header/>
            <div class="w-100 d-block d-md-none px-4 mb-3">
              <home-button/>
            </div>
            <opening-hours v-if="availableResourceOpenSlots.length === 0"/>
            <open-slot-list v-if="availableResourceOpenSlots.length > 0" :slots="availableResourceOpenSlots" :resources="resources"/>
          </div>
          <div class="fixed-buttons d-none d-md-block">
            <home-button/>
          </div>
        </div>
        <div class="booking-divider booking-divider-right">
          <display-banner/>
          <div class="d-flex flex-column justify-content-center align-items-center my-5 px-4">
            <div class="error-banner-area">
                <error-banner v-show="error" :message="error" class="mb-0"/>
            </div>
            <email-input v-model="email"/>
            <password-input v-model="password"
                class="mt-2"
                @keydownEnter="tryLogin"
                placeholder="Password">
            </password-input>
            <div v-if="recaptchaEnabled" class="mt-2">
                <turnstile-widget ref="turnstileWidget" execution="execute" @verified="handleRecaptchaToken" @errored="handleRecaptchaError" @expired="clearRecaptcha" @timedOut="clearRecaptcha" @retrying="clearRecaptcha" @interactiveStart="pauseRecaptchaWait" @interactiveEnd="resumeRecaptchaWait"></turnstile-widget>
                <validation-message v-if="recaptchaMessage" :message="recaptchaMessage"></validation-message>
            </div>
            <button class="btn btn-secondary w-100  mt-2"
                @click="tryLogin"
                :disabled="disabled">
                <i class="fas fa-sign-in-alt"></i>
                Login
                <span v-if="loading || pendingRecaptcha" class="inline-loader"></span>
            </button>
            <button class="btn btn-outline-tertiary w-100 mt-2"
                @click="gotoForgotPassword">
                Forgot Password?
            </button>
          </div>
        </div>
      </div>
    </div>
  </settings-checker>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { ErrorHelper } from '../../helpers/ErrorHelper'
import { OpenSlotCacheMixin } from '@/mixins/Booking/OpenSlotCacheMixin'
import { RecaptchaSettingsCacheMixin } from '@/mixins/Booking/RecaptchaSettingsCacheMixin'
import { TurnstileExecuteMixin } from '@/mixins/Booking/TurnstileExecuteMixin'
import OpenSlotList from '../Booking/OpenSlotList.vue'
import EmailInput from '../../components/Layout/EmailInput.vue'
import vueCookies from 'vue-cookies'
import ErrorBanner from '../ErrorValidation/ErrorBanner.vue'
import PetstarRibbon from '../Booking/PetstarRibbon.vue'
import DisplayBanner from '../Booking/DisplayBanner.vue'
import SettingsChecker from '../Booking/SettingsChecker.vue'
import Header from '../Booking/Header.vue'
import OpeningHours from '../Booking/OpeningHours.vue'
import HomeButton from '../Booking/HomeButton.vue'
import PasswordInput from './PasswordInput.vue'
import TurnstileWidget from '../Booking/TurnstileWidget.vue'
import ValidationMessage from '../ErrorValidation/ValidationMessage.vue'

export default {
  name: 'Authentication',
  mixins: [OpenSlotCacheMixin, RecaptchaSettingsCacheMixin, TurnstileExecuteMixin],
  data () {
    return {
      error: '',
      password: '',
      email: '',
      rememberEmail: false
    }
  },
  computed: {
    ...mapState('Authentication', [
      'loading',
      'user'
    ]),
    loginModel () {
      return {
        email: this.email,
        password: this.password,
        recaptcha: this.recaptcha
      }
    },
    credentialsValid () {
      return !!this.email && !!this.password
    },
    disabled () {
      return !!this.loading || this.pendingRecaptcha || !this.credentialsValid
    },
    rememberedEmail: {
      get () {
        return vueCookies.get('email')
      },
      set (value) {
        vueCookies.set('email', value, -1, null, null, true, 'Strict')
      }
    }
  },
  methods: {
    ...mapActions('Authentication', [
      'login',
      'logout'
    ]),
    tryLogin () {
      const self = this
      if (self.loading || self.pendingRecaptcha || !self.credentialsValid) {
        return
      }
      self.error = ''
      if (self.recaptchaEnabled && !self.recaptcha) {
        self.requestRecaptcha(self.performLogin)
        return
      }
      self.performLogin()
    },
    performLogin () {
      const self = this
      self.login({ siteId: self.selectedSiteId, request: self.loginModel })
        .then(() => {
          if (self.rememberEmail) {
            self.rememberedEmail = self.email
          } else {
            vueCookies.remove('email')
          }
          const query = { siteId: self.selectedSiteId }
          if (self.postId) query.postId = self.postId
          self.$router.push({ name: 'booking', query })
        })
        .catch(error => {
          self.error = ErrorHelper.isNotAuthenticated(error) ? 'Invalid username or password' : ErrorHelper.getError(error)
          self.resetRecaptcha()
        })
    },
    refreshEmail () {
      this.email = this.rememberedEmail || ''
      this.rememberEmail = !!this.email
    },
    gotoForgotPassword () {
      const query = { siteId: this.selectedSiteId }
      if (this.postId) query.postId = this.postId
      this.$router.push({ name: 'forgotPassword', query })
    }
  },
  created () {
    this.refreshEmail()
    this.logout()
  },
  components: {
    'opening-hours': OpeningHours,
    'open-slot-list': OpenSlotList,
    'booking-header': Header,
    'settings-checker': SettingsChecker,
    'error-banner': ErrorBanner,
    'email-input': EmailInput,
    'petstar-ribbon': PetstarRibbon,
    'display-banner': DisplayBanner,
    'home-button': HomeButton,
    'password-input': PasswordInput,
    'turnstile-widget': TurnstileWidget,
    'validation-message': ValidationMessage
  }
}
</script>

<style scoped>
@media (max-width: 768px) {
  .booking-divider-right {
    height: max-content;
    min-height: 50vh !important;
  }
}
</style>
